import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Image from 'next/image';
import Link from 'next/link';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {cdnImage} from '@/util/cdnImage';
import {contentMaxWidth, borderRadius, colors, fontSize, fontWeight, gradients, spacing} from '@/styles/tokens';
import {imgSizes} from '@/util/imageSizes';

const missionImage = cdnImage('about-us-new/Our-Vision.webp');

interface PillarCard {
    title: string;
    description: string;
    points: string[];
    bgColor: string;
    titleColor: string;
    bodyColor: string;
    checkColor: string;
}

const cards: PillarCard[] = [
    {
        title: 'Our Mission',
        description:
            'To help businesses turn their ideas into reliable digital products that solve real challenges and create lasting business value.',
        points: [
            'Build solutions around clear business goals',
            'Take ownership of delivery and quality',
            'Maintain clear communication throughout every project',
            'Deliver products built for long-term growth'
        ],
        bgColor: colors.background.missionCard,
        titleColor: colors.text.primary,
        bodyColor: colors.text.primary,
        checkColor: colors.text.primary
    },
    {
        title: 'Our Vision',
        description:
            'To become a trusted global technology partner known for helping businesses turn complex challenges into meaningful digital progress.',
        points: [
            'Build lasting relationships with our clients',
            'Help businesses adapt to changing technology needs',
            'Create solutions that support sustainable growth',
            'Set higher standards for digital product delivery'
        ],
        bgColor: colors.background.visionCard,
        titleColor: colors.text.primary,
        bodyColor: colors.text.primary,
        checkColor: colors.text.primary
    },
    {
        title: 'Our Values & Beliefs',
        description: 'Our values guide how we work with clients, approach challenges, and build solutions that businesses can rely on.',
        points: [
            'Accountability: We take ownership of our commitments.',
            'Transparency: We keep clients informed at every stage.',
            'Quality: We build with reliability and long-term use in mind.',
            'Purpose: We use technology to solve meaningful business problems.'
        ],
        bgColor: colors.primary.deep,
        titleColor: colors.text.inverse,
        bodyColor: colors.text.inverse,
        checkColor: colors.text.inverse
    }
];

const OurMission = () => {
    return (
        <Box component="section" sx={{py: spacing[64], backgroundColor: colors.background.slate50}}>
            <MUIContainer>
                {/* Hero row */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {xs: 'column', md: 'row'},
                        alignItems: 'center',
                        gap: {xs: 5, md: 8},
                        mb: {xs: 6, md: 8}
                    }}
                >
                    {/* Left text */}
                    <Box sx={{flex: 1, width: '100%'}}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 1,
                                border: `1px solid ${colors.neutral.slate300}`,
                                borderRadius: borderRadius.full,
                                px: 2,
                                py: 0.75,
                                mb: 3,
                                backgroundColor: colors.background.default
                            }}
                        >
                            <Box sx={{width: 6, height: 6, borderRadius: borderRadius.circle, backgroundColor: colors.primary.darkNavy}} />
                            <Typography
                                className="font-regular-text"
                                sx={{fontSize: fontSize[16], color: colors.text.primary, fontWeight: fontWeight.medium}}
                            >
                                Our Core Commitments
                            </Typography>
                        </Box>

                        <Typography
                            component="h2"
                            className="font-headers"
                            sx={{
                                fontSize: {xs: fontSize[32], sm: fontSize[40], md: fontSize[48]},
                                lineHeight: 1.15,
                                mb: 3
                            }}
                        >
                            Our Commitment to Meaningful Innovation
                        </Typography>

                        <Typography
                            className="font-regular-text"
                            sx={{
                                fontSize: fontSize[16],
                                color: colors.text.primary,
                                lineHeight: 1.8,
                                mb: 4
                            }}
                        >
                            At Debut Infotech, we believe technology should solve real business problems and create lasting value. Our
                            mission gives us direction while our vision shapes the future we want to build. Our values guide how we work
                            with clients and deliver solutions that support their business goals.
                        </Typography>

                        <Button
                            component={Link}
                            href="https://www.debutinfotech.com/contact-us"
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            className="font-headers"
                            sx={{
                                background: gradients.primaryButton,
                                color: colors.text.inverse,
                                borderRadius: borderRadius.md,
                                px: 3,
                                py: 1.5,
                                textTransform: 'none',
                                fontWeight: fontWeight.semibold,
                                fontSize: fontSize[16],
                                boxShadow: 'none',
                                '&:hover': {background: colors.primary.buttonHover, boxShadow: 'none'}
                            }}
                        >
                            Discuss Your Project
                        </Button>
                    </Box>

                    {/* Right image */}
                    <Box
                        sx={{
                            width: {xs: '100%', md: '50%'},
                            maxWidth: contentMaxWidth.narrow,
                            flexShrink: 0,
                            position: 'relative',
                            aspectRatio: '4 / 3',
                            borderRadius: borderRadius['3xl'],
                            overflow: 'hidden'
                        }}
                    >
                        <Image
                            src={missionImage}
                            alt="Debut Infotech team in a strategy meeting"
                            fill
                            sizes={imgSizes({xs: '100vw', md: '50vw', lg: '750px'})}
                            style={{objectFit: 'cover', objectPosition: 'center'}}
                        />
                    </Box>
                </Box>

                {/* Pillar cards */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {xs: '1fr', md: 'repeat(3, 1fr)'},
                        gap: {xs: 3, md: 3}
                    }}
                >
                    {cards.map((card) => (
                        <Box
                            key={card.title}
                            sx={{
                                backgroundColor: card.bgColor,
                                borderRadius: borderRadius['2xl'],
                                p: {xs: 3, md: 4},
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            <Typography
                                component="h3"
                                className="font-headers"
                                sx={{
                                    fontSize: {xs: fontSize[18], sm: fontSize[20], md: fontSize[24]},
                                    fontWeight: fontWeight.bold,
                                    color: card.titleColor,
                                    mb: 2
                                }}
                            >
                                {card.title}
                            </Typography>
                            <Typography
                                className="font-regular-text"
                                sx={{
                                    fontSize: fontSize[16],
                                    color: card.bodyColor,
                                    lineHeight: 1.7,
                                    mb: 2
                                }}
                            >
                                {card.description}
                            </Typography>
                            <Box
                                component="ul"
                                sx={{
                                    listStyle: 'none',
                                    p: 0,
                                    m: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    mt: 'auto'
                                }}
                            >
                                {card.points.map((point) => (
                                    <Box component="li" key={point} sx={{display: 'flex', alignItems: 'flex-start', gap: 1.25}}>
                                        <CheckIcon sx={{fontSize: fontSize[18], color: card.checkColor, mt: '2px', flexShrink: 0}} />
                                        <Typography
                                            className="font-regular-text"
                                            sx={{
                                                fontSize: fontSize[16],
                                                color: card.bodyColor,
                                                lineHeight: 1.6
                                            }}
                                        >
                                            {point}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(OurMission);
